import { createAsyncThunk, createSlice, PayloadAction, isAnyOf } from '@reduxjs/toolkit';
import { okaoAxios } from '../../helpers/axios';
import { RootState, store } from '../../redux/store';
import { Restaurant, Summary, AdminRestaurant } from './types'
import { addNewMenu, addMenu, updateMenu, saveDish, deleteDish, publishMenu, uploadDishImage, deleteDishImage, deleteMenu } from '../Menu/slice';
import { attachDishImageSuggestion } from '../Menu/dishImageSuggestionsSlice';
import { addPress } from '../Reviews/slice';
import { AlertColor } from '@mui/material/Alert';


export interface IRestaurantSlice {
  restaurants?: Restaurant[] | undefined;
  allRestaurantsWithMenus?:Restaurant[]|undefined;
  adminrestaurants?: AdminRestaurant[] | undefined;
  currentRestaurant?: Restaurant | undefined;
  newRestaurantCount: number
  status: string
  count: number
  needsWebsiteCount: number
  needsTagsAndDescriptions: number
  countByStatus: Summary[] | undefined
  topCities: Summary[] | undefined
  shouldOpen: boolean
  message: string | undefined
  severity: AlertColor | undefined
  currentPage: number
}

const initialState: IRestaurantSlice = {
  restaurants: undefined,
  newRestaurantCount: 0,
  status: 'idle',
  count: 0,
  needsWebsiteCount: 0,
  needsTagsAndDescriptions: 0,
  countByStatus: undefined,
  topCities: undefined,
  shouldOpen: false,
  message: undefined,
  severity: undefined,
  currentPage: 0
};

export const fetchRestaurants = createAsyncThunk('api/fetchRestaurants', async (page: number) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).get(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/restaurants/${page}?size=${store.getState().dashboard.pageSize}`)
  return response;
})

export const fetchRestaurantsAdminDashboard = createAsyncThunk('api/fetchRestaurantAdminDashboards', async (page: number) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).get(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/restaurants/admin/${page}?size=${store.getState().dashboard.pageSize}`)
  return response;
})

export const fetchRestaurantByAlias = createAsyncThunk('api/fetchRestaurantByAlias', async (alias: string) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).get(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/restaurants/${alias}/details`)
  return response;
})

export const fetchRestaurantsWithMenus = createAsyncThunk('api/fetchRestaurantsWithMenus', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).get(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/restaurants/all-with-menus`)
  return response;
})

export const generateTagsAndDescriptions = createAsyncThunk('api/generateTagsAndDescriptions', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/tags-descriptions`));
  return response;
})

export const generateRestaurantImages = createAsyncThunk('api/generateRestaurantImages', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/dalle-restaurant-images`));
  return response;
})

export const generateDishImages = createAsyncThunk('api/generateDishImages', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/ingestion/dalle-dish-images`));
  return response;
})

export const publishAllRestaurants = createAsyncThunk('api/publishAllRestaurants', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/restaurants/publish-all`));
  return response;
})

export const loadReviews = createAsyncThunk('api/loadAllReviews', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/restaurants/load-all-reviews`));
  return response;
})

export const saveRestaurant = createAsyncThunk('api/saveRestaurant', async (nmr: Restaurant) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/restaurants`, nmr));
  return response;
})

export const loadReview = createAsyncThunk('api/loadReview', async (restaurantId: string) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/restaurants/loadreviews/${restaurantId}`));
  return response;
})

export const publishRestaurant = createAsyncThunk('api/publishRestaurant', async (restaurantId: string) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/restaurants/publish/${restaurantId}`));
  return response;
})

export const deleteRestaurant = createAsyncThunk('api/deleteRestaurant', async (restaurantId: string) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).delete(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/restaurants/${restaurantId}`));
  return response;
})

// Backfill images from Google
export const backfillRestaurantImages = createAsyncThunk<Record<string, never>, { restaurantId: string; overwriteExisting: boolean }>(
  'api/backfillRestaurantImages',
  async ({ restaurantId, overwriteExisting }) => {
    const auth = store.getState().secure.accessToken ?? '';
    await okaoAxios(auth).post(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/restaurants/${restaurantId}/images/backfill?overwriteExisting=${overwriteExisting}`
    );
    return {};
  }
);

export const restaurantSlice = createSlice({
  name: 'restaurant',
  initialState,
  reducers: {
    setRestaurants: (state, action: PayloadAction<Restaurant[] | undefined>) => {
        state.restaurants = action.payload
    },
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.shouldOpen = action.payload
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload
    },
    setCurrentRestaurant: (state, action: PayloadAction<Restaurant>) => {
      state.currentRestaurant = action.payload
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchRestaurants.fulfilled, (state, action) => {
      state.restaurants = action.payload.data.result
      state.count = action.payload.data.count
      state.newRestaurantCount = action.payload.data.newRestaurantCount
      state.needsWebsiteCount = action.payload.data.needsWebsiteCount
      state.needsTagsAndDescriptions = action.payload.data.needsTagsAndDescriptionsCount
      state.countByStatus = action.payload.data.countByStatus
      state.topCities = action.payload.data.topCities
      state.status = 'idle'
    }).addCase(fetchRestaurantsAdminDashboard.fulfilled, (state, action) => {
      state.adminrestaurants = action.payload.data.result
      state.count = action.payload.data.count
      state.newRestaurantCount = action.payload.data.newRestaurantCount
      state.needsWebsiteCount = action.payload.data.needsWebsiteCount
      state.needsTagsAndDescriptions = action.payload.data.needsTagsAndDescriptionsCount
      state.countByStatus = action.payload.data.countByStatus
      state.topCities = action.payload.data.topCities
      state.status = 'idle'
    }).addCase(fetchRestaurantsWithMenus.fulfilled, (state, action) => {
      state.allRestaurantsWithMenus = action.payload.data as Restaurant[]
      state.status = 'idle'
    })
    .addCase(saveRestaurant.fulfilled, (state, action) => {
      const restaurant: Restaurant = action.payload.data as Restaurant
      state.currentRestaurant = restaurant
      state.shouldOpen = true;
      state.message = 'Successfully Saved Restaurant'
      state.status = 'idle'
      state.severity = 'success'
    }).addCase(fetchRestaurantByAlias.fulfilled, (state, action) => {
      const restaurant: Restaurant = action.payload.data as Restaurant
      state.currentRestaurant = restaurant;
      state.status = 'idle'
    });
    
    // Add addNewMenu handler - check if it exists to handle circular dependency
    // This can happen in test environments due to module loading order
    // Use a safe check to avoid errors when addNewMenu is undefined
    const addNewMenuFulfilled = (addNewMenu as any)?.fulfilled;
    if (addNewMenuFulfilled) {
      builder.addCase(addNewMenuFulfilled, (state, action) => {
        const restaurant: Restaurant = action.payload.data as Restaurant
        state.currentRestaurant = restaurant
        state.shouldOpen = true;
        state.message = 'Successfully Added Menu'
        state.status = 'idle'
        state.severity = 'success'
      });
    }
    
    builder.addCase(loadReview.fulfilled, (state, action) => {
      const restaurant: Restaurant = action.payload.data as Restaurant
      state.currentRestaurant = restaurant
      state.shouldOpen = true;
      state.message = 'Successfully Loaded Reviews'
      state.status = 'idle'
      state.severity = 'success'
    });
    
    // Add handlers for Menu slice thunks - use safe checks to handle circular dependencies in tests
    const saveDishFulfilled = (saveDish as any)?.fulfilled;
    if (saveDishFulfilled) {
      builder.addCase(saveDishFulfilled, (state, action) => {
        const restaurant: Restaurant = action.payload.data as Restaurant
        state.currentRestaurant = restaurant
        state.shouldOpen = true;
        state.message = 'Successfully Saved Dish'
        state.status = 'idle'
        state.severity = 'success'
      });
    }
    
    const updateMenuFulfilled = (updateMenu as any)?.fulfilled;
    if (updateMenuFulfilled) {
      builder.addCase(updateMenuFulfilled, (state, action) => {
        const restaurant: Restaurant = action.payload.data as Restaurant
        state.currentRestaurant = restaurant
        state.shouldOpen = true;
        state.message = 'Successfully Updated'
        state.status = 'idle'
        state.severity = 'success'
      });
    }
    
    builder.addCase(addPress.fulfilled, (state, action) => {
      const restaurant: Restaurant = action.payload.data as Restaurant
      state.currentRestaurant = restaurant
      state.message = 'Successfully Added Press'
      state.status = 'idle'
      state.severity = 'success'
    })
    const publishMenuFulfilled = (publishMenu as any)?.fulfilled;
    if (publishMenuFulfilled) {
      builder.addCase(publishMenuFulfilled, (state, action) => {
        const restaurant: Restaurant = action.payload.data as Restaurant
        state.currentRestaurant = restaurant
        state.shouldOpen = true;
        state.message = 'Successfully Published Menu'
        state.status = 'idle'
        state.severity = 'success'
      });
    }
    
    const deleteMenuFulfilled = (deleteMenu as any)?.fulfilled;
    if (deleteMenuFulfilled) {
      builder.addCase(deleteMenuFulfilled, (state, action) => {
      const deletedMenuId = action.meta.arg; // menuId passed into thunk
      if (state.currentRestaurant?.menus != null) {
        state.currentRestaurant.menus = state.currentRestaurant.menus.filter(
          (m) => m.menuID !== deletedMenuId
        );
      }
      // Also update allRestaurantsWithMenus if it exists
      if (state.allRestaurantsWithMenus != null) {
        state.allRestaurantsWithMenus = state.allRestaurantsWithMenus.map(restaurant => {
          if (restaurant.menus != null) {
            return {
              ...restaurant,
              menus: restaurant.menus.filter(m => m.menuID !== deletedMenuId)
            };
          }
          return restaurant;
        });
      }
      state.shouldOpen = true;
      state.message = 'Menu deleted successfully';
      state.status = 'idle';
      state.severity = 'success';
      });
    }
    
    const uploadDishImageFulfilled = (uploadDishImage as any)?.fulfilled;
    if (uploadDishImageFulfilled) {
      builder.addCase(uploadDishImageFulfilled, (state, action) => {
      const { dishId, imageUrl } = action.payload;
      if (state.currentRestaurant?.menus != null) {
        state.currentRestaurant.menus.forEach(menu => {
          const dish = menu.dishes?.find(d => d.dishID === dishId);
          if (dish != null) {
            dish.imageUrl = imageUrl;
          }
        });
      }
      state.status = 'idle';
      state.shouldOpen = true;
      state.message = 'Dish image uploaded successfully';
      state.severity = 'success';
      });
    }
    
    const deleteDishImageFulfilled = (deleteDishImage as any)?.fulfilled;
    if (deleteDishImageFulfilled) {
      builder.addCase(deleteDishImageFulfilled, (state, action) => {
      const { dishId } = action.payload;
      if (state.currentRestaurant?.menus != null) {
        state.currentRestaurant.menus.forEach(menu => {
          const dish = menu.dishes?.find(d => d.dishID === dishId);
          if (dish != null) {
            dish.imageUrl = null;
          }
        });
      }
      state.status = 'idle';
      state.shouldOpen = true;
      state.message = 'Dish image removed successfully';
      state.severity = 'success';
      });
    }
    
    builder.addCase(attachDishImageSuggestion.fulfilled, (state) => {
      state.status = 'idle';
      state.shouldOpen = true;
      state.message = 'Dish image updated from suggestion successfully';
      state.severity = 'success';
    })
    .addCase(attachDishImageSuggestion.rejected, (state, action) => {
      state.status = 'error';
      state.shouldOpen = true;
      state.message = action.error.message ?? 'Failed to attach suggested image';
      state.severity = 'error';
    })
    .addCase(backfillRestaurantImages.pending, (state) => {
      state.status = 'loading';
    })
    .addCase(backfillRestaurantImages.fulfilled, (state) => {
      state.status = 'idle';
      state.shouldOpen = true;
      state.message = 'Image backfill completed successfully';
      state.severity = 'success';
    })
    .addCase(backfillRestaurantImages.rejected, (state, action) => {
      state.status = 'error';
      state.shouldOpen = true;
      state.message = action.error.message ?? 'Failed to backfill images from Google';
      state.severity = 'error';
    })
    // Filter out undefined thunks for addMatcher to avoid circular dependency issues in tests
    const rejectedActions = [
      fetchRestaurantsAdminDashboard.rejected,
      fetchRestaurantByAlias.rejected,
      loadReview.rejected,
      addPress.rejected,
      saveRestaurant.rejected,
      publishAllRestaurants.rejected,
      loadReviews.rejected,
      publishRestaurant.rejected,
      deleteRestaurant.rejected,
      fetchRestaurants.rejected,
      generateTagsAndDescriptions.rejected,
      generateDishImages.rejected,
      generateRestaurantImages.rejected,
      backfillRestaurantImages.rejected,
      attachDishImageSuggestion.rejected,
    ].filter(Boolean) as any[];
    
    // Add Menu slice thunks if they exist
    if (deleteDish?.rejected) rejectedActions.push(deleteDish.rejected);
    if (publishMenu?.rejected) rejectedActions.push(publishMenu.rejected);
    if (updateMenu?.rejected) rejectedActions.push(updateMenu.rejected);
    if (saveDish?.rejected) rejectedActions.push(saveDish.rejected);
    if (addMenu?.rejected) rejectedActions.push(addMenu.rejected);
    if (uploadDishImage?.rejected) rejectedActions.push(uploadDishImage.rejected);
    if (deleteDishImage?.rejected) rejectedActions.push(deleteDishImage.rejected);
    if (deleteMenu?.rejected) rejectedActions.push(deleteMenu.rejected);
    
    builder.addMatcher(isAnyOf(...rejectedActions), (state, action) => {
      state.shouldOpen = true;
      state.message = 'Error occurred while performing a restaurant action'
      state.status = 'error'
      state.severity = 'error'
      console.log(action.error);
    });
    
    // Filter out undefined thunks for pending matcher
    const pendingActions = [
      fetchRestaurantsAdminDashboard.pending,
      fetchRestaurantByAlias.pending,
      loadReview.pending,
      addPress.pending,
      fetchRestaurants.pending,
      publishAllRestaurants.pending,
      loadReviews.pending,
      saveRestaurant.pending,
      publishRestaurant.pending,
      deleteRestaurant.pending,
      generateTagsAndDescriptions.pending,
      generateDishImages.pending,
      generateRestaurantImages.pending,
      attachDishImageSuggestion.pending,
    ].filter(Boolean) as any[];
    
    // Add Menu slice thunks if they exist
    if (addNewMenu?.pending) pendingActions.push(addNewMenu.pending);
    if (deleteDish?.pending) pendingActions.push(deleteDish.pending);
    if (publishMenu?.pending) pendingActions.push(publishMenu.pending);
    if (updateMenu?.pending) pendingActions.push(updateMenu.pending);
    if (saveDish?.pending) pendingActions.push(saveDish.pending);
    if (addMenu?.pending) pendingActions.push(addMenu.pending);
    if (uploadDishImage?.pending) pendingActions.push(uploadDishImage.pending);
    if (deleteDishImage?.pending) pendingActions.push(deleteDishImage.pending);
    if (deleteMenu?.pending) pendingActions.push(deleteMenu.pending);
    
    builder.addMatcher(isAnyOf(...pendingActions), (state, action) => {
      state.status = 'loading'
    })
    // Filter out undefined thunks for fulfilled matcher
    const fulfilledActions = [
      publishAllRestaurants.fulfilled,
      loadReviews.fulfilled,
      publishRestaurant.fulfilled,
      deleteRestaurant.fulfilled,
      generateTagsAndDescriptions.fulfilled,
      generateDishImages.fulfilled,
      generateRestaurantImages.fulfilled,
    ].filter(Boolean) as any[];
    
    // Add Menu slice thunks if they exist
    if (deleteDish?.fulfilled) fulfilledActions.push(deleteDish.fulfilled);
    if (addMenu?.fulfilled) fulfilledActions.push(addMenu.fulfilled);
    
    builder.addMatcher(isAnyOf(...fulfilledActions), (state, action) => {
      state.status = 'idle'
      state.shouldOpen = true
      state.severity = 'success'
      state.message = "Success"
    })
  }
});

export const selectRestaurants = (state: RootState): Restaurant[] => state.restaurant.restaurants ?? []
export const selectAdminRestaurants = (state: RootState): AdminRestaurant[] => state.restaurant.adminrestaurants ?? []
export const selectRestaurantsWithMenus = (state: RootState): Restaurant[] => state.restaurant.allRestaurantsWithMenus ?? []
export const selectCurrentRestaurant = (state: RootState): Restaurant | undefined => state.restaurant.currentRestaurant ?? undefined
export const selectRestaurantWithMenuByAlias = (alias: string) => (state: RootState): Restaurant | undefined => state.restaurant.allRestaurantsWithMenus?.find(restaurant => restaurant.alias === alias) ?? undefined
export const selectRowCount = (state: RootState): number => state.restaurant.count
export const selectNewRestaurantCount = (state: RootState): number => state.restaurant.newRestaurantCount
export const selectIsLoading = (state: RootState): boolean => state.restaurant.status === "loading"
export const selectNeedsWebsiteCount = (state: RootState): number => state.restaurant.needsWebsiteCount
export const selectNeedsTagsAndDescriptions = (state: RootState): number => state.restaurant.needsTagsAndDescriptions
export const selectCountByStatus = (state: RootState): Summary[] | undefined => state.restaurant.countByStatus
export const selectTopCities = (state: RootState): Summary[] | undefined => state.restaurant.topCities
export const selectShouldOpen = (state: RootState): boolean => state.restaurant.shouldOpen
export const selectMessage = (state: RootState): string | undefined => state.restaurant.message
export const selectSeverity = (state: RootState): AlertColor | undefined => state.restaurant.severity
export const selectCurrentPage = (state: RootState): number => state.restaurant.currentPage

export const { setRestaurants, setOpen, setCurrentPage, setCurrentRestaurant } = restaurantSlice.actions;
export default restaurantSlice.reducer
